import { Action, Reducer } from 'redux';
import { STATE } from 'app/constants';
import { withOptionsReducer } from 'app/components/options-handler';
import {
  ACTION,
  UpdateSavedFiltersConfig, UpdateFilterValuesTranslation,
  UpdateFilterValues, UpdateFilterMenuValues, UpdateFilterValidation, ClearAllFilters, OpenCloseFilter,
  UpdateQuery, UpdateQueryValue, SetQueryValues, ClearQuery
} from './action-types';
import { SearchFiltersState } from './types-store';
import { FilterValues } from './types';

export const INITIAL_SEARCH_FILTERS_STATE: SearchFiltersState = {
  savedFiltersConfig: { area: undefined },
  query: '',
  options: undefined,
  queryValues: [],
  filterValues: {},
  filterMenuValues: {},
  filterValidation: [],
  filterOpen: {}
};

const reducers: { [indexer: string]: Reducer<SearchFiltersState, Action> } = {

  [ACTION.UPDATE_SAVED_FILTERS_CONFIG]: (state: SearchFiltersState, action: UpdateSavedFiltersConfig): SearchFiltersState => {

    if (state.savedFiltersConfig === action.filtersConfig) {
      return state;
    }

    return {
      ...state,
      savedFiltersConfig: action.filtersConfig
    };
  },

  [ACTION.UPDATE_FILTER_VALUES_TRANSLATION]: (state: SearchFiltersState, action: UpdateFilterValuesTranslation): SearchFiltersState => {

    const filterValues = Object.keys(state.filterValues ?? {})?.reduce(
      (result, key) => {
        const currentValues = state.filterValues[key];

        const values = action.filterValues?.[key];
        const newValues = currentValues.map(value => {

          const updatedFilter = values?.find(updatedValue => updatedValue.value === value.value);
          const updatedDescription = updatedFilter?.description;
          const updatedTitle = updatedFilter?.title;

          return {
            ...value,
            ...(!!updatedDescription && updatedDescription !== value.description && { description: updatedDescription }),
            ...(!!updatedTitle && updatedTitle !== value.title && { title: updatedTitle })
          };
        });

        return {
          ...result,
          [key]: newValues
        };
      },
      {} as FilterValues
    );

    return {
      ...state,
      filterValues
    };
  },

  [ACTION.UPDATE_FILTER_VALUES]: (state: SearchFiltersState, action: UpdateFilterValues): SearchFiltersState => {

    const values = action.values && action.values.length > 0 ? action.values : undefined;
    const currentValues = state.filterValues?.[action.id];

    if (values === currentValues) {
      return state;
    }

    if (!values || !values[0] || values[0].value === undefined || values[0].value === '') {
      const { [action.id]: _, ...filteredValues } = state.filterValues;
      return {
        ...state,
        filterValues: filteredValues
      };
    }

    return {
      ...state,
      filterValues: {
        ...state.filterValues,
        [action.id]: values
      }
    };
  },

  [ACTION.UPDATE_FILTER_MENU_VALUES]: (state: SearchFiltersState, action: UpdateFilterMenuValues): SearchFiltersState => {

    if (state.filterMenuValues[action.id] === action.values) {
      return state;
    }

    return {
      ...state,
      filterMenuValues: {
        ...state.filterMenuValues,
        [action.id]: action.values
      }
    };
  },

  [ACTION.UPDATE_FILTER_VALIDATION]: (state: SearchFiltersState, action: UpdateFilterValidation): SearchFiltersState => {

    const existingValidation = state.filterValidation.find(validation => validation.id === action.filterValidation.id);

    let filterValidation;

    if (existingValidation) {
      filterValidation = state.filterValidation.map(validation => validation.id === existingValidation.id ? {
        id: existingValidation.id,
        validation: action.filterValidation.validation
      } : validation);
    } else {
      filterValidation = [
        ...state.filterValidation,
        action.filterValidation
      ];
    }

    return {
      ...state,
      filterValidation
    };
  },

  [ACTION.CLEAR_ALL_FILTERS]: (state: SearchFiltersState, action: ClearAllFilters): SearchFiltersState => {

    return {
      ...state,
      filterValues: action.initialDefaultFilterValues,
      filterMenuValues: {},
      filterOpen: {}
    };
  },

  [ACTION.OPEN_CLOSE_FILTER]: (state: SearchFiltersState, action: OpenCloseFilter): SearchFiltersState => {

    return {
      ...state,
      filterOpen: {
        ...state.filterOpen,
        [action.id]: action.open
      }
    };
  },

  [ACTION.UPDATE_QUERY]: (state: SearchFiltersState, action: UpdateQuery): SearchFiltersState => {

    return {
      ...state,
      query: action.query
    };
  },

  [ACTION.UPDATE_QUERY_VALUE]: (state: SearchFiltersState, action: UpdateQueryValue): SearchFiltersState => {

    return {
      ...state,
      queryValues: state.queryValues.some(o => o.value === action.value.value)
        ? state.queryValues.filter(o => o.value !== action.value.value)
        : [...state.queryValues, action.value]
    };
  },

  [ACTION.UPDATE_QUERY_VALUES]: (state: SearchFiltersState, action: SetQueryValues): SearchFiltersState => {

    return {
      ...state,
      queryValues: action.values
    };
  },

  [ACTION.CLEAR_QUERY]: (state: SearchFiltersState, action: ClearQuery): SearchFiltersState => {

    return {
      ...state,
      query: '',
      queryValues: []
    };
  }
};

const stateReducer: Reducer<SearchFiltersState> =
  (state: SearchFiltersState = INITIAL_SEARCH_FILTERS_STATE, action: any) => {
    const r = reducers[action.type];
    return (r && r(state, action)) || state;
  };

export const reducer = withOptionsReducer(STATE.searchFilters, stateReducer);
